import axios from 'axios'
import dayjs from "dayjs";
import { useFormik } from 'formik'
import { enqueueSnackbar } from "notistack";
import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useHistory } from 'react-router-dom'
import * as yup from 'yup'
import { API } from "../../../helpers/constants";
import { handleAxiosErrors } from "../../../helpers/errorHandling";
import { getRouteUrl } from '../../../helpers/routing/getRouteUrl'
import useUserData from "../../../hooks/user/useUserData";
import { USERS } from "../locales/namespaces";
import {
    ROUTE_PAGE_USERS_CREATE,
    ROUTE_PAGE_USERS_DETAILS,
    ROUTE_PAGE_USERS_EDIT,
    ROUTE_PAGE_USERS_INDEX
} from '../routes/routes'

const useUserForm = ({ id, user, staticData, costs, role, showFields }) => {

    const history = useHistory()
    const { t } = useTranslation(USERS)
    const [showPassword, setShowPassword] = useState(false)
    const [loadingSubmit, setLoadingSubmit] = useState(false)
    const {token} = useUserData()
    
    const onSubmit = (values) => {
        setLoadingSubmit(true)
        axios({
            method: 'post',
            data: {
                UserId: id,
                isActive: values.isActive,
                username: values.username,
                LoginName: values.LoginName,
                Code: values.Code,
                password: values.password,
                passwordConfirmation: values.password,
                email: values.email,
                FatherName: values.FatherName,
                RoleId: values.RoleId,
                skills: values.userSkills,
                costs: values.costs.map((c) => ({
                    ExtraWorkCost: Number(c.ExtraWorkCost),
                    FromDt: dayjs(c.FromDt).format('YYYY-MM-DD'), // "YYYY-MM-DD"
                    // ToDt: new Date(c.ToDt).toISOString().split('T')[0], // "YYYY-MM-DD"
                    WorkCost: Number(c.WorkCost)
                })),
            },
            headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
            url: !id ? `${API}/en/user/store` : `${API}/en/user/update`
        }).then(response => {
            setLoadingSubmit(false)
            enqueueSnackbar(t(`User ${!id ? 'created' : 'updated'} successfully!`), {
                variant: 'success',
                autoHideDuration: 3000,
            });
            if (!id) {
                formik.resetForm(); //  Καθαρισμός της φόρμας
                history.push(getRouteUrl(ROUTE_PAGE_USERS_CREATE, { role }));
                // window.location.reload(); // αναγκαστική ανανέωση
            } else {
                history.push(getRouteUrl(ROUTE_PAGE_USERS_INDEX, {role}));
            }
            // history.push(getRouteUrl(ROUTE_PAGE_USERS_EDIT, { id: !id ? response.data.userid : id, role:role }))

        }).catch((error) => {
            setLoadingSubmit(false)
            if (error?.response?.data?.errors === "There are overlapping intervals.") {
                enqueueSnackbar(t('There is a record within this date range!'), {
                    variant: 'error',
                    autoHideDuration: 1700,
                })
            } else {
                enqueueSnackbar(handleAxiosErrors(error, t('An error occurred!')), {
                    variant: 'warning',
                    autoHideDuration: 3000,
                })
            }
        })
    }

    const validationSchema = yup.object().shape({
        LoginName: yup.string().required(t('Required')),
       ...(showFields && {RoleId: yup.string().required(t('Required'))}),
        ...(role === '1' && {costs: yup.array().min(1, t('Required')).required(t('Required'))}),
        username: yup.string().when('RoleId', { //εδώ κανουμε έλεγχο. Αν ο ρολος δεν ειναι ένας απο τους δύο τοτε καντο υποχρεωτικό, αλλιώς προαιρετικό
            is: (role) => showFields,
            then: (schema) => schema.required(t('Required')),
            otherwise: (schema) => schema.notRequired()
        }),
        password: yup.string().when('RoleId', (role, schema) => {
            const check = role && role.length > 0 ? role[0] : 0
            if (check !== "2" && check !== "3") {
                if (!id) {
                    // Δημιουργία χρήστη -> απαιτείται
                    return schema.required(t('Required')).matches(/^.{6,20}$/, t('Password must be between 6-20 characters'));
                } else {
                    // Επεξεργασία -> όχι required αλλά αν δοθεί, να ελέγχεται
                    return schema.test(
                        'password-length-if-present',
                        t('Password must be between 6-20 characters'),
                        function (value) {
                            if (!value) return true;
                            return /^.{6,20}$/.test(value);
                        }
                    );
                }
            }
            return schema.notRequired();
        }),
        passwordConfirmation: yup.string().when('RoleId', (role, schema) => {
            const check = role && role.length > 0 ? role[0] : 0
            if (check !== "2" && check !== "3") {
                return schema.test(
                    'confirmation-matches-password',
                    t('Passwords must match'),
                    function (value) {
                        const { password } = this.parent;
                        if (!password && !value) return true;
                        return password === value;
                    }
                );
            }
            return schema.notRequired();
        }),
        email: yup.string().email(t('Enter a valid email')),
    })

    const formik = useFormik({
        initialValues: {
            isActive: id ? user?.isActive : true,
            username: user?.username || '',
            LoginName: user?.LoginName || '',
            Code: user?.Code || '',
            password: '',
            passwordConfirmation: '',
            UserImage: user?.UserImage || '',
            email: user?.email || '',
            FatherName: user?.FatherName || '',
            RoleId: (Number(role) === 1) ? staticData.roles?.[0]?.id :  user?.RoleId ? user?.RoleId : '',
            userSkills: user?.userSkills ? user.userSkills : [],
            costs: user?.costs && user?.costs.length > 0 ? user?.costs.map(cost => ({
                id: cost.id,
                FromDt: dayjs(cost?.FromDt),
                // ToDt: dayjs(cost?.ToDt),
                WorkCost: cost.WorkCost,
                ExtraWorkCost:cost.ExtraWorkCost,
            })) : [],
        },
        validationSchema: validationSchema,
        onSubmit: onSubmit,
        validateOnChange: false,
        // validateOnBlur: false,
        // enableReinitialize:true
    })

    const handleClickShowPassword = () => setShowPassword((show) => !show)
    const handleMouseDownPassword = (event) => {
        event.preventDefault()
    }

    return {
        formik,
        showPassword,
        handleClickShowPassword,
        handleMouseDownPassword,
        loadingSubmit
    }

}
export default useUserForm